// ========================================
// ERROR HANDLING SYSTEM
// Sistema centralizado de manejo de errores para agentes
// ========================================

/**
 * Códigos de error estandarizados
 */
const ERROR_CODES = {
    // API y Configuración
    API_KEY_MISSING: 'ERR_001',
    API_KEY_INVALID: 'ERR_002',
    API_QUOTA_EXCEEDED: 'ERR_003',
    API_REQUEST_FAILED: 'ERR_004',
    API_TIMEOUT: 'ERR_005',

    // Respuestas de IA
    INVALID_JSON_RESPONSE: 'ERR_101',
    EMPTY_RESPONSE: 'ERR_102',
    MALFORMED_RESPONSE: 'ERR_103',

    // Validación Matemática
    MATH_VALIDATION_FAILED: 'ERR_201',
    INVALID_NUMBER_RANGE: 'ERR_202',
    NEGATIVE_SUBTRACTION: 'ERR_203',
    CARRYING_NOT_ALLOWED: 'ERR_204',

    // Generación de Contenido
    IMAGE_GENERATION_FAILED: 'ERR_301',
    IMAGE_GENERATION_TIMEOUT: 'ERR_302',
    NARRATIVE_GENERATION_FAILED: 'ERR_303',

    // Configuración y Estado
    INVALID_CONFIGURATION: 'ERR_401',
    MISSING_REQUIRED_FIELD: 'ERR_402',
    CONTEXT_NOT_FOUND: 'ERR_403'
};

/**
 * Clase de error personalizada para agentes
 */
class AgentError extends Error {
    /**
     * @param {string} code - Código del error (de ERROR_CODES)
     * @param {string} message - Mensaje descriptivo
     * @param {Object} details - Detalles adicionales del error
     */
    constructor(code, message, details = {}) {
        super(message);
        this.name = 'AgentError';
        this.code = code;
        this.details = details;
        this.timestamp = new Date().toISOString();

        // Mantener stack trace apropiado
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AgentError);
        }
    }

    /**
     * Genera una representación JSON del error
     */
    toJSON() {
        return {
            name: this.name,
            code: this.code,
            message: this.message,
            details: this.details,
            timestamp: this.timestamp,
            stack: this.stack
        };
    }

    /**
     * Genera una representación legible del error
     */
    toString() {
        return `[${this.code}] ${this.message}`;
    }
}

/**
 * Maneja errores de agentes con fallback automático
 * @param {Error} error - Error a manejar
 * @param {Function} fallbackFn - Función de fallback a ejecutar
 * @param {string} agentName - Nombre del agente (para logging)
 * @returns {*} Resultado del fallback
 */
function handleAgentError(error, fallbackFn, agentName = 'Agent') {
    // Log del error
    if (error instanceof AgentError) {
        console.error(`❌ [${agentName}] ${error.toString()}`);
        console.error('Detalles:', error.details);
    } else {
        console.error(`❌ [${agentName}] Error inesperado:`, error);
    }

    // Ejecutar fallback
    console.warn(`⚠️ [${agentName}] Ejecutando fallback...`);
    try {
        return fallbackFn();
    } catch (fallbackError) {
        console.error(`❌ [${agentName}] Fallback también falló:`, fallbackError);
        throw new AgentError(
            ERROR_CODES.INVALID_CONFIGURATION,
            `Tanto la operación principal como el fallback fallaron en ${agentName}`,
            { originalError: error.message, fallbackError: fallbackError.message }
        );
    }
}

/**
 * Valida que la API key esté configurada
 * @throws {AgentError} Si la API key no está configurada
 */
function validateApiKey() {
    const config = (typeof window !== 'undefined' && window.GEMINI_CONFIG) 
        ? window.GEMINI_CONFIG 
        : (typeof GEMINI_CONFIG !== 'undefined' ? GEMINI_CONFIG : null);

    if (!config) {
        throw new AgentError(
            ERROR_CODES.API_KEY_MISSING,
            'Configuración de GEMINI_CONFIG no encontrada',
            { hint: 'Verifica que config/api.js esté cargado correctamente' }
        );
    }

    if (!config.apiKey || config.apiKey === 'YOUR_API_KEY_HERE') {
        throw new AgentError(
            ERROR_CODES.API_KEY_MISSING,
            'API key de Gemini no configurada',
            { hint: 'Configura GEMINI_CONFIG.apiKey en config/api.js' }
        );
    }

    if (config.apiKey.length < 20) {
        throw new AgentError(
            ERROR_CODES.API_KEY_INVALID,
            'API key parece inválida (demasiado corta)',
            { keyLength: config.apiKey.length }
        );
    }
}

/**
 * Valida respuesta JSON de la API
 * @param {Object} response - Respuesta de fetch()
 * @param {Object} data - Datos parseados
 * @throws {AgentError} Si la respuesta es inválida
 */
function validateApiResponse(response, data) {
    if (!response.ok) {
        // Determinar tipo de error específico
        if (response.status === 429) {
            throw new AgentError(
                ERROR_CODES.API_QUOTA_EXCEEDED,
                'Has excedido la cuota de la API de Gemini',
                { status: response.status, hint: 'Espera unos minutos o verifica tu plan' }
            );
        } else if (response.status === 401 || response.status === 403) {
            throw new AgentError(
                ERROR_CODES.API_KEY_INVALID,
                'API key inválida o sin permisos',
                { status: response.status }
            );
        } else {
            throw new AgentError(
                ERROR_CODES.API_REQUEST_FAILED,
                `Request a API falló con status ${response.status}`,
                { status: response.status, statusText: response.statusText }
            );
        }
    }

    if (!data || !data.candidates || data.candidates.length === 0) {
        throw new AgentError(
            ERROR_CODES.EMPTY_RESPONSE,
            'La API no devolvió candidatos',
            { receivedData: data }
        );
    }

    const text = data.candidates[0]?.content?.parts?.[0]?.text;
    if (!text) {
        throw new AgentError(
            ERROR_CODES.EMPTY_RESPONSE,
            'La respuesta de la API está vacía',
            { candidate: data.candidates[0] }
        );
    }
}

/**
 * Valida que un objeto JSON tenga los campos requeridos
 * @param {Object} obj - Objeto a validar
 * @param {Array<string>} requiredFields - Campos requeridos
 * @param {string} objectName - Nombre del objeto (para mensajes)
 * @throws {AgentError} Si faltan campos
 */
function validateRequiredFields(obj, requiredFields, objectName = 'objeto') {
    const missing = requiredFields.filter(field => !(field in obj));

    if (missing.length > 0) {
        throw new AgentError(
            ERROR_CODES.MISSING_REQUIRED_FIELD,
            `Faltan campos requeridos en ${objectName}`,
            { missingFields: missing, received: Object.keys(obj) }
        );
    }
}

/**
 * Valida rango numérico según nivel LOMLOE
 * @param {number} value - Valor a validar
 * @param {number} min - Mínimo permitido
 * @param {number} max - Máximo permitido
 * @param {string} fieldName - Nombre del campo
 * @throws {AgentError} Si está fuera de rango
 */
function validateNumberRange(value, min, max, fieldName = 'número') {
    if (value < min || value > max) {
        throw new AgentError(
            ERROR_CODES.INVALID_NUMBER_RANGE,
            `${fieldName} fuera de rango permitido`,
            { value, min, max, hint: `Debe estar entre ${min} y ${max}` }
        );
    }
}

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.ERROR_CODES = ERROR_CODES;
    window.AgentError = AgentError;
    window.handleAgentError = handleAgentError;
    window.validateApiKey = validateApiKey;
    window.validateApiResponse = validateApiResponse;
    window.validateRequiredFields = validateRequiredFields;
    window.validateNumberRange = validateNumberRange;
}

console.log('✅ [ErrorHandling] Sistema de errores cargado');
